/* eslint-disable max-depth */
/* eslint-disable max-statements */
import axios from 'axios';

/**
 * Google Places API (New) client for fetching AI-powered place summaries
 * and nearby points of interest.
 *
 * Documentation: https://developers.google.com/maps/documentation/places/web-service/place-summaries
 */

const GOOGLE_PLACES_API_BASE = 'https://places.googleapis.com/v1';
const API_TIMEOUT = 10000; // 10 seconds

// ============================================================================
// CACHING LAYER
// ============================================================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class GooglePlacesCache {
  private cache: Map<string, CacheEntry<any>>;
  private readonly TTL: number;
  private readonly MAX_ENTRIES: number;

  constructor(ttlMinutes = 60, maxEntries = 100) {
    this.cache = new Map();
    this.TTL = ttlMinutes * 60 * 1000; // Convert to milliseconds
    this.MAX_ENTRIES = maxEntries;
  }

  /**
   * Generate cache key from coordinates (rounded to ~100m precision)
   * This groups nearby searches together to maximize cache hits
   */
  private generateKey(latitude: number, longitude: number, radius: number): string {
    // Round to 3 decimal places (~111m precision)
    // This means searches within ~100m will share cache
    const lat = Math.round(latitude * 1000) / 1000;
    const lng = Math.round(longitude * 1000) / 1000;
    return `places:${lat},${lng}:${radius}`;
  }

  /**
   * Get cached data if available and not expired
   */
  get<T>(latitude: number, longitude: number, radius: number): T | null {
    const key = this.generateKey(latitude, longitude, radius);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Store data in cache with TTL
   */
  set<T>(latitude: number, longitude: number, radius: number, data: T): void {
    const key = this.generateKey(latitude, longitude, radius);

    // Enforce max entries (LRU-style cleanup)
    if (this.cache.size >= this.MAX_ENTRIES) {
      const firstKey = this.cache.keys().next().value || '';
      this.cache.delete(firstKey);
    }

    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + this.TTL,
    });
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.MAX_ENTRIES,
      entries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        ageMinutes: Math.round((Date.now() - entry.timestamp) / 60000),
        expiresInMinutes: Math.round((entry.expiresAt - Date.now()) / 60000),
      })),
    };
  }
}

// Global cache instance - 60 minute TTL, max 100 entries
const placesCache = new GooglePlacesCache(60, 100);

// Export for debugging/testing
export const clearGooglePlacesCache = () => {
  placesCache.clear();
};

export const getGooglePlacesCacheStats = () => placesCache.getStats();

// Expose cache management to browser console for debugging
if (typeof window !== 'undefined') {
  (window as any).__googlePlacesCache = {
    stats: getGooglePlacesCacheStats,
    clear: clearGooglePlacesCache,
    help: () => {
      // Debug commands available in browser console
    },
  };
}

// ============================================================================
// END CACHING LAYER
// ============================================================================

/**
 * Get the Google Places API key from environment variables.
 * SSR-safe: uses process.env in Node.js, import.meta.env in browser.
 */
function getGooglePlacesApiKey(): string {
  // In SSR context (Node.js), use process.env
  if (typeof window === 'undefined') {
    return process.env.VUE_GOOGLE_PLACES_NEIGHBORHOOD_API_KEY || '';
  }
  // In browser context, use import.meta.env
  return import.meta.env.VUE_GOOGLE_PLACES_NEIGHBORHOOD_API_KEY || '';
}

// Types for Google Places API responses
interface PlaceGenerativeSummary {
  overview?: {
    text: string;
    languageCode: string;
  };
  overviewFlagContentUri?: string;
  disclosureText?: {
    text: string;
    languageCode: string;
  };
}

interface PlaceResult {
  id: string;
  displayName?: {
    text: string;
    languageCode: string;
  };
  formattedAddress?: string;
  types?: string[];
  rating?: number;
  userRatingCount?: number;
  generativeSummary?: PlaceGenerativeSummary;
  location?: {
    latitude: number;
    longitude: number;
  };
  primaryType?: string;
  primaryTypeDisplayName?: {
    text: string;
    languageCode: string;
  };
}

interface NearbySearchResponse {
  places: PlaceResult[];
}

/**
 * Calculate distance between two coordinates using Haversine formula.
 * Returns distance in meters.
 */
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Get enriched context about a location by searching for key amenities.
 * This data can be used to enhance the AI prompt with real, specific information.
 * Results are cached for 60 minutes to reduce API calls.
 */
export async function getLocationContext(params: {
  latitude: number;
  longitude: number;
  radius?: number;
}): Promise<{
  restaurants: PlaceResult[];
  schools: PlaceResult[];
  parks: PlaceResult[];
  transit: PlaceResult[];
  shopping: PlaceResult[];
} | null> {
  const { latitude, longitude, radius = 1000 } = params;

  // ============================================================================
  // CHECK CACHE FIRST
  // ============================================================================
  const cachedData = placesCache.get<{
    restaurants: PlaceResult[];
    schools: PlaceResult[];
    parks: PlaceResult[];
    transit: PlaceResult[];
    shopping: PlaceResult[];
  }>(latitude, longitude, radius);

  if (cachedData) {
    return cachedData;
  }

  const apiKey = import.meta.env.VUE_GOOGLE_MAPS_PDP_API_KEY;

  if (!apiKey) {
    return null;
  }

  // Check if API key is a placeholder
  if (apiKey.includes('GooglePlacesAPIKey') || apiKey.length < 30) {
    return null;
  }

  try {
    // Fetch different categories in parallel
    const [restaurants, schools, parks, transit, shopping] = await Promise.all([
      searchPlacesByType({
        latitude,
        longitude,
        radius,
        types: ['restaurant', 'cafe'],
        maxResults: 5,
      }),
      searchPlacesByType({
        latitude,
        longitude,
        radius,
        types: ['school', 'primary_school'],
        maxResults: 3,
      }),
      searchPlacesByType({ latitude, longitude, radius, types: ['park'], maxResults: 3 }),
      searchPlacesByType({
        latitude,
        longitude,
        radius,
        types: ['transit_station', 'train_station', 'subway_station', 'bus_station'],
        maxResults: 3,
      }),
      searchPlacesByType({
        latitude,
        longitude,
        radius,
        types: ['supermarket', 'shopping_mall'],
        maxResults: 3,
      }),
    ]);

    const result = {
      restaurants: restaurants || [],
      schools: schools || [],
      parks: parks || [],
      transit: transit || [],
      shopping: shopping || [],
    };

    // Cache the result
    placesCache.set(latitude, longitude, radius, result);

    return result;
  } catch (error) {
    console.error('❌ Google Places API location context error:', error);
    return null;
  }
}

/**
 * Helper function to search places by specific types.
 * Falls back to old Places API if new API is not available.
 */
async function searchPlacesByType(params: {
  latitude: number;
  longitude: number;
  radius: number;
  types: string[];
  maxResults: number;
}): Promise<PlaceResult[] | null> {
  const { latitude, longitude, radius, types, maxResults } = params;

  const apiKey = import.meta.env.VUE_GOOGLE_PLACES_NEIGHBORHOOD_API_KEY || '';

  if (!apiKey) return null;

  try {
    // Try new Places API first
    const response = await axios.post<NearbySearchResponse>(
      `${GOOGLE_PLACES_API_BASE}/places:searchNearby`,
      {
        maxResultCount: maxResults,
        locationRestriction: {
          circle: {
            center: { latitude, longitude },
            radius,
          },
        },
        includedTypes: types,
        rankPreference: 'POPULARITY',
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask':
            'places.id,places.displayName,places.formattedAddress,places.types,places.rating,places.userRatingCount,places.generativeSummary,places.primaryType',
        },
        timeout: API_TIMEOUT,
        validateStatus: (status) => status < 500,
      },
    );

    if (response.status === 200) {
      return response.data.places || [];
    }

    // If new API fails with 403, fall back to old Places API
    if (response.status === 403) {
      return await searchPlacesByTypeOldAPI({ latitude, longitude, radius, types, maxResults });
    }

    return null;
  } catch (error) {
    // Try old API as fallback
    try {
      return await searchPlacesByTypeOldAPI({ latitude, longitude, radius, types, maxResults });
    } catch (fallbackError) {
      return null;
    }
  }
}

/**
 * Fallback to old Places API (Nearby Search).
 */
async function searchPlacesByTypeOldAPI(params: {
  latitude: number;
  longitude: number;
  radius: number;
  types: string[];
  maxResults: number;
}): Promise<PlaceResult[] | null> {
  const { latitude, longitude, radius, types } = params;
  const apiKey = import.meta.env.VUE_GOOGLE_MAPS_PDP_API_KEY;

  if (!apiKey) return null;

  try {
    const response = await axios.get(
      'https://maps.googleapis.com/maps/api/place/nearbysearch/json',
      {
        params: {
          location: `${latitude},${longitude}`,
          radius,
          type: types[0], // Old API only supports one type at a time
          key: apiKey,
        },
        timeout: API_TIMEOUT,
      },
    );

    if (response.data.status === 'OK' && response.data.results) {
      // Transform old API response to match new API format
      return response.data.results.map((place: any) => ({
        id: place.place_id,
        displayName: {
          text: place.name,
          languageCode: 'en',
        },
        formattedAddress: place.vicinity,
        types: place.types,
        rating: place.rating,
        userRatingCount: place.user_ratings_total,
        location: place.geometry?.location
          ? {
              latitude: place.geometry.location.lat,
              longitude: place.geometry.location.lng,
            }
          : undefined,
        primaryType: place.types?.[0],
      }));
    }

    return null;
  } catch (error) {
    return null;
  }
}
